import React, { FC, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
    fetchEnrollmentAndEnrolleeDetails,
    updateEnrollmentDetails,
} from '@actions/memberPortal/enrollmentsActionCreators';
import { GICheckbox, GIFormControl, GIFormLabel } from '@components/common/app-components/GIInput';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import { TXN_REASON, TXN_TYPES } from '@constants/enrollmentList/enrollmentList.constant';
import { GENDER_TYPES } from '@constants/ssap/miscellaneous/miscellaneous';
import { useAppDispatch } from '@hooks/index';
import useSetSelectedEnrollment from '@hooks/useSetSelectedEnrollment';
import { formatDateToMMDDYYYY } from '@utils/dates/dateMethods';
import { GIButton } from 'app-components';

import { logWarning } from '../../../../utils/tools/ErrorLoggers';
import useGetSelectedEnrollmentDetails from '../../hooks/useGetSelectedEnrollmentDetails';
import { changeFieldValue, isYearEndDate } from '../common/enrollmentUtils';
import { IEnrollee, IRootState, ITransactionCategories } from '../common/types';
import { getEnrollmentEditToolErrorMessage } from '../common/validation';
import { AddOrChangeTxnFormContext } from '../context/AddOrChangeTxnProvider';
import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';
import useEnrollmentModal from '../hooks/useEnrollmentModal';
import TerminationModal from '../modals/TerminationModal';
import { StyledCheckboxField, StyledFooterContainer } from '../styles/EnrollmentSharedStyled';

const AddOrChangeTxnFooter: FC = () => {
    const dispatch = useAppDispatch();
    const { id } = useParams();
    const getSelectedEnrollmentDetails = useGetSelectedEnrollmentDetails();
    const permissions = useSelector((state: IRootState) => state.memberEnrollment?.permissions);
    const [showTerminationModal, setShowTerminationModal] = useState(false);
    const { userPermissions } = permissions || {};
    const {
        watch,
        setValue,
        formState: { isValid, isDirty, errors },
    } = useContext(AddOrChangeTxnFormContext);
    const setSelectedEnrollment = useSetSelectedEnrollment();

    const { selectedEnrollment, isEdit, setIsEdit, setViewAddOrChangeTxn } = useContext(EnrollmentContext);
    const values = watch();
    const { handleShowResult } = useEnrollmentModal();

    const handleClose = (): void => {
        setIsEdit(false);
        setViewAddOrChangeTxn(false);
    };

    const handleCheckBoxClick = (field: string, value: boolean): void => {
        changeFieldValue(setValue, field, value);
    };

    const validateAndSaveAddOrChangeTxn = () => {
        if (isEdit) {
            if (modalConfig.showModal) {
                setShowTerminationModal(true);
            } else {
                sendAsChangeTransaction();
            }
        } else {
            sendAsAddTransaction();
        }
    };

    const sendAsAddTransaction = async (): Promise<void> => {
        let data: any = {
            id: values.enrollmentId,
            householdId: selectedEnrollment.houseHoldCaseId,
            comment: values.addOrChangeComment,
            send834: values.addOrChangeSendIn834,
            sendTermNotice: values.sendTermNoticeVM,
            benefitEffectiveDateStr: values.enrollmentBenefitEffectiveStartDateUI,
            aptcAmount: values.aptcAmount !== '' ? parseFloat(values.aptcAmount) : null,
            action: TXN_TYPES.Add,
            members: [],
        };

        values.enrolleeDataDtoList.forEach(enrolleeDetails => {
            if (enrolleeDetails.txnType === true) {
                data.members.push({
                    exchgIndivIdentifier: enrolleeDetails.memberId,
                    enrolleeId: enrolleeDetails.enrolleeId,
                });
            }
        });
        try {
            const response = await updateEnrollmentDetails(dispatch, data);
            onResponse(response);
        } catch (error) {
            logWarning('Error updating enrollment:', error);
        }
    };

    const sendAsChangeTransaction = async (): Promise<void> => {
        let data: any = {
            id: values.enrollmentId,
            householdId: selectedEnrollment.houseHoldCaseId,
            comment: values.addOrChangeComment,
            send834: values.addOrChangeSendIn834,
            sendTermNotice: values.sendTermNoticeVM,
            benefitEffectiveDate: null,
            benefitEndDate: null,
            benefitEffectiveDateStr: values.enrollmentBenefitEffectiveStartDateUI,
            benefitEndDateStr: values.enrollmentBenefitEffectiveEndDateUI,
            confirmIfNotConfirmed: false,
            premiumPaidToDateEndStr: formatDateToMMDDYYYY(values.enrollmentLastPremPaidThroughDateUI),
            premiumPaidToDateEnd: null,
            members: [],
            updateSLCP: false,
            enrollmentPremiumDtoList: values.enrollmentPremiumDtoList,
        };

        if (values.enrollmentLevelTxnType === TXN_TYPES.Term) {
            let txnReason = TXN_REASON.AI;
            values.enrolleeDataDtoList.forEach(enrollee => {
                if (enrollee.relationshipToSubscriber?.toUpperCase() === RELATIONSHIP_TYPES.SELF.value) {
                    txnReason = enrollee.txnReason;
                }
            });
            values.enrolleeDataDtoList.forEach(enrollee => {
                enrollee.txnReason = txnReason;
            });
        }

        values.enrolleeDataDtoList.forEach(function (enrolleeDetails) {
            data.members.push({
                exchgIndivIdentifier: enrolleeDetails.memberId,
                enrolleeId: enrolleeDetails.enrolleeId,
                txnType: enrolleeDetails.txnType,
                txnReason: enrolleeDetails.txnReason,
                effectiveStartDateStr: enrolleeDetails.enrolleeEffectiveStartDateUI,
                effectiveEndDateStr: enrolleeDetails.enrolleeEffectiveEndDateUI,
                firstName: enrolleeDetails.firstName,
                lastName: enrolleeDetails.lastName,
                middleName: enrolleeDetails.middleName,
                gender: genderLookupCode(enrolleeDetails),
                birthDate: enrolleeDetails.birthDate ? new Date(enrolleeDetails.birthDate).toLocaleDateString() : '',
                homeZip: enrolleeDetails.enrolleeZipcode,
            });
        });

        var newPremium = values.enrollmentPremiumDtoList;
        var oldPremium = selectedEnrollment.enrollmentPremiumDtoList;
        data.updateSLCP = newPremium.some(
            (newPrem, i) =>
                newPrem.month === oldPremium[i].month &&
                newPrem.slcspPremiumAmount !== oldPremium[i].slcspPremiumAmount,
        );

        try {
            const response = await updateEnrollmentDetails(dispatch, data);
            await onResponse(response);
        } catch (error) {
            logWarning('Error updating enrollment:', error);
        } finally {
            closeModal();
        }
    };

    const onResponse = async response => {
        if (response.errCode === 200) {
            const message = getEnrollmentEditToolErrorMessage(response.errCode, '');
            handleShowResult({
                isSuccess: true,
                isError: false,
                message: message,
                handleSubmit: onModalClose,
            });

            const formData = new FormData();
            formData.append('encHouseholdId', id || '');
            formData.append('csrftoken', window.csrf_token);
            const enrollmentDetails = await dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
            if (enrollmentDetails) {
                const enrollment = await getSelectedEnrollmentDetails(
                    selectedEnrollment.enrollmentId,
                    enrollmentDetails,
                );
                setSelectedEnrollment(enrollment);
            }
        } else {
            const message = getEnrollmentEditToolErrorMessage(response.errCode, '');
            handleShowResult({
                isSuccess: false,
                isError: true,
                message: message,
            });
        }
    };

    const onModalClose = () => {
        setIsEdit(false);
        setViewAddOrChangeTxn(false);
    };

    const genderLookupCode = (value: any) => {
        switch (value.genderLookupLabel.toLowerCase()) {
            case GENDER_TYPES.MALE:
                return 'M';
            case GENDER_TYPES.FEMALE:
                return 'F';
            default:
                return 'U';
        }
    };

    const closeModal = () => {
        setShowTerminationModal(false);
    };

    const transactionCategories = useMemo(() => {
        const list = values.enrolleeDataDtoList ?? [];
        const categories: ITransactionCategories = {
            termWithYearEnd: [],
            termWithoutYearEnd: [],
            changeWithYearEnd: [],
            changeWithoutYearEnd: [],
        };

        list.forEach(enrollee => {
            const { enrolleeEffectiveEndDateUI: endDate, txnType } = enrollee;
            if (!endDate) return;

            const isYearEnd = isYearEndDate(endDate);

            if (txnType === TXN_TYPES.Term) {
                if (isYearEnd) {
                    categories.termWithYearEnd.push(enrollee);
                } else {
                    categories.termWithoutYearEnd.push(enrollee);
                }
            } else if (txnType === TXN_TYPES.Change) {
                if (isYearEnd) {
                    categories.changeWithYearEnd.push(enrollee);
                } else {
                    categories.changeWithoutYearEnd.push(enrollee);
                }
            }
        });

        return categories;
    }, [values.enrolleeDataDtoList]);

    const isSubscriberTerminationOnYearEnd = useMemo(() => {
        const subscriber = values.enrolleeDataDtoList?.find(
            enrollee => enrollee.relationshipToSubscriber?.toUpperCase() === RELATIONSHIP_TYPES.SELF.value,
        );

        if (!subscriber) return false;

        const { enrolleeEffectiveEndDateUI: endDate, txnType } = subscriber;
        return endDate && isYearEndDate(endDate) && txnType === TXN_TYPES.Term;
    }, [values.enrolleeDataDtoList]);

    const modalConfig = useMemo(() => {
        if (!transactionCategories) {
            return { showModal: false, isTermination: false };
        }

        const { termWithYearEnd, changeWithYearEnd, changeWithoutYearEnd } = transactionCategories;

        const hasItems = (items: IEnrollee[]) => items.length > 0;

        const hasTermYearEnd = hasItems(termWithYearEnd);
        const hasChangeYearEnd = hasItems(changeWithYearEnd);
        const hasChangeNonYearEnd = hasItems(changeWithoutYearEnd);

        if (isSubscriberTerminationOnYearEnd) {
            return { showModal: true, isTermination: true };
        }

        if (hasChangeNonYearEnd) {
            return { showModal: true, isTermination: false };
        }

        if (hasTermYearEnd && hasChangeYearEnd) {
            return { showModal: true, isTermination: true };
        }

        if (hasTermYearEnd) {
            return { showModal: true, isTermination: true };
        }

        return { showModal: false, isTermination: false };
    }, [transactionCategories, isSubscriberTerminationOnYearEnd]);

    return (
        <>
            <StyledFooterContainer data-testid="footer-container">
                <StyledCheckboxField>
                    <GIFormControl
                        id="sendInTxnTypeForm"
                        name={'member.enrollment_list.premiumhistory.sendIn'}
                        type="checkbox"
                    >
                        <GICheckbox
                            id={'member.enrollment_list.premiumhistory.sendIn'}
                            checked={values.addOrChangeSendIn834}
                            onChange={e => handleCheckBoxClick('addOrChangeSendIn834', e.target.checked)}
                            name={'member.enrollment_list.premiumhistory.sendIn'}
                            value={values.addOrChangeSendIn834}
                            disabled={values.isTermReasonNonPayment}
                        />
                        <GIFormLabel
                            id={'member.enrollment_list.premiumhistory.sendIn'}
                            name={'member.enrollment_list.premiumhistory.sendIn'}
                        >
                            <FormattedMessage id="member.enrollment_list.premiumhistory.sendIn" />
                        </GIFormLabel>
                    </GIFormControl>
                </StyledCheckboxField>

                <ConditionalRendering isTrue={userPermissions?.sendTermFlag}>
                    <StyledCheckboxField>
                        <GIFormControl id="sendInTxnTypeForm" name={'sendTermNoticeVM'} type="checkbox">
                            <GICheckbox
                                id={'sendTermNoticeVM'}
                                checked={values.sendTermNoticeVM}
                                onChange={e => handleCheckBoxClick('sendTermNoticeVM', e.target.checked)}
                                name={'sendTermNoticeVM'}
                                value={values.sendTermNoticeVM}
                                disabled={values.isTermReasonNonPayment}
                            />
                            <GIFormLabel id={'sendTermNoticeVM'} name={'sendTermNoticeVM'}>
                                <FormattedMessage id="member.enrollment_list.premiumhistory.sentTermNotice" />
                            </GIFormLabel>
                        </GIFormControl>
                    </StyledCheckboxField>
                </ConditionalRendering>

                <GIButton
                    isDisabled={!isValid || !isDirty}
                    id="button"
                    onClickHandler={validateAndSaveAddOrChangeTxn}
                    variant="primary"
                    labelId="button.submit"
                    data-testid="submit-button"
                />
                <GIButton
                    id="button"
                    onClickHandler={handleClose}
                    variant="secondary"
                    labelId="button.cancel"
                    data-testid="cancel-button"
                />
            </StyledFooterContainer>
            <ConditionalRendering isTrue={showTerminationModal}>
                <TerminationModal
                    closeModal={closeModal}
                    onContinueClick={sendAsChangeTransaction}
                    isTermination={modalConfig.isTermination}
                />
            </ConditionalRendering>
        </>
    );
};
export default AddOrChangeTxnFooter;
